import { useEffect, useRef, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useProgressoAjuda } from "@/core/hooks/useProgressoAjuda";
import { useFecharComEsc } from "@/shared/hooks/useFecharComEsc";
import { LICOES_BASE } from "@/core/ajuda/licoes";
import { deveMostrarBoasVindas } from "@/core/ajuda/progresso";
import { ConteudoApresentacao } from "@/core/components/ajuda/ConteudoApresentacao";
import { PAGINA_AJUDA } from "@/core/components/ajuda/ajudaUi";

const FOCAVEIS = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

/**
 * Tela de boas-vindas da Ajuda do Elos, por cima do Início no primeiro acesso (como no protótipo
 * aprovado). Não bloqueia o uso: "Pular por agora", a tecla Esc e "Começar" a fecham, e a resposta
 * fica guardada neste aparelho (não há nada no banco). Quem pula continua vendo o cartão "Novo
 * por aqui?" no Início, e a Ajuda segue no menu da conta.
 *
 * Limite conhecido: sem guardar nada na conta, o Elos só sabe "esta pessoa ainda não respondeu NESTE
 * aparelho". Quem já usava o Elos antes também vê a tela uma vez, e um aparelho novo mostra de novo.
 */
export function BoasVindasAjuda() {
  const navigate = useNavigate();
  const { progresso, pronto, concluirBoasVindas } = useProgressoAjuda();
  const raizRef = useRef<HTMLDivElement>(null);
  // A apresentação fala só das lições para todos; as de quem lidera ou gerencia têm cartão próprio no Início.
  const total = LICOES_BASE.length;
  const aberto = pronto && deveMostrarBoasVindas(progresso);

  useFecharComEsc(aberto, concluirBoasVindas);

  // A tela de trás não rola enquanto a boas-vindas está aberta.
  useEffect(() => {
    if (!aberto) return;
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = antes;
    };
  }, [aberto]);

  // O Tab não sai da janela: só há os dois botões, então basta circular entre o primeiro e o último.
  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    const itens = Array.from(raizRef.current?.querySelectorAll<HTMLElement>(FOCAVEIS) ?? []);
    const primeiro = itens[0];
    const ultimo = itens[itens.length - 1];
    if (!primeiro || !ultimo) return;
    const ativo = document.activeElement;
    if (e.shiftKey && (ativo === primeiro || !raizRef.current?.contains(ativo))) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && ativo === ultimo) {
      e.preventDefault();
      primeiro.focus();
    }
  }

  if (!aberto) return null;

  return (
    <div
      ref={raizRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="boas-vindas-titulo"
      onKeyDown={aoTeclar}
      className="fixed inset-0 z-50 overflow-y-auto bg-bg"
    >
      <div className={`${PAGINA_AJUDA} flex min-h-full flex-col justify-center`}>
        <ConteudoApresentacao
          totalLicoes={total}
          nivelTitulo="h2"
          idTitulo="boas-vindas-titulo"
          onComecar={() => {
            concluirBoasVindas();
            navigate("/ajuda/conhecendo");
          }}
          onPular={concluirBoasVindas}
        />
      </div>
    </div>
  );
}
